import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ky from 'ky';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { TextField, Button, Typography, Grid, Card } from '@material-ui/core';

import { GoodsKindType, GoodsDb } from '../redux/shopSlice';
import { setSnackbar } from '../redux/workBackendSlice';
import useMakePublishData, { PublishDataType } from '../hooks/useMakePublishData';

import { Hash, toSnakeCase, toCamelCase, isCorrectKey, getCookie } from '../general';
import codeToText from '../general/codeToText';
import { formatVersion } from '.';
import { apiURL } from '../components/DigimagiProvider';
import SelectVersionDialog from './SelectVersionDialog';

type LatestInfoType = [number, string, string]
export type GoodsDataType = {
  goods: GoodsDb & { id: string; };
  items: Hash<Object[]>;
  thisInfo: [number, string];
  allLatestInfo: LatestInfoType;
  majorLatestInfo: LatestInfoType;
  otherLatestsInfo: LatestInfoType[];
}

type ChangeLogListProps = {
  changeLog: Hash<{ dataKind: string; nestIds: string[]; }>;
  ids: string[];
  itemDataList: Hash<{ dataKind: string; }>;
}
const ChangeLogList: React.FC<ChangeLogListProps> = (props) => {
  const { changeLog, ids, itemDataList } = props;

  return (
    <>
      { ids.map(id => {
        const changed = changeLog[id];
        const base = itemDataList[id];
        if (!changed) return;
        let name = '';
        if (base && 'name' in base && typeof base.name === 'string') name = `[ ${base.name} ]`;
        else if ('name' in changed && typeof changed.name === 'string') name = `[ ${changed.name} ]`;
        const dataKind = base ? base.dataKind : changed.dataKind;
        return (
          <div style={{ marginBottom: '8px' }}>
            <Typography variant="caption">- { codeToText(dataKind, 'ja') } { name } ({ id })</Typography>
            <div style={{ marginLeft: '16px' }}>
              { Object.keys(changed).map(propKey => {
                  if (!isCorrectKey(changed, propKey) || propKey === 'nestIds' || propKey === 'dataKind') return;
                  const changedValue = changed[propKey];
                  const baseValue = isCorrectKey(base, propKey) ? base[propKey] : null;
                  if (propKey === 'component_status') return (
                    <Typography variant="caption">{ changedValue === 'added' ? '追加' : '削除' }</Typography>
                  )
                  if (base) return (
                    <Grid container>
                      <Grid item xs={2}>
                        <Typography variant="caption">{ propKey }</Typography>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography variant="body1">{ JSON.stringify(baseValue) } → { JSON.stringify(changedValue) }</Typography>
                      </Grid>
                    </Grid>
                  );
              }) }
              { changed.nestIds.length >= 1 && (
                <div>
                  <ChangeLogList changeLog={changeLog} ids={changed.nestIds} itemDataList={itemDataList} />
                </div>
              ) }
            </div>
          </div>
        );
      })}
    </>
  );
}

type PublishDialogProps = {
  open: boolean;
  onClose: () => void;
  goodsKind: GoodsKindType; 
  id?: string;
}
const PublishDialog: React.FC<PublishDialogProps> = (props) => {
  const dispatch = useDispatch();
  const { open, onClose, goodsKind, id } = props;
  const [goodsData, setGoodsData] = useState<GoodsDataType>();
  const [name, setName] = useState<string>();
  const [status, setStatus] = useState<string>('');
  const [thisVersionInNumber, setThisVersionInNumber] = useState<number>();
  const makePublishData = useMakePublishData();
  const [publishData, setPublishData] = useState<PublishDataType>();
  const publishStatus: Hash<string> = { 'public': '公開', 'private': '限定公開', 'closed': '非公開' };
  const [changeLog, setChangeLog] = useState<Hash<{ dataKind: string; nestIds: string[]; }>>({});
  const [itemDataList, setItemDataList] = useState<Hash<{ dataKind: string; }>>({});
  const [primeIds, setPrimeIds] = useState<string[]>([]);
  const [updateData, setUpdateData] = useState<Hash<Object>>();
  const [selectVersionAction, setSelectVersionAction] = useState(false);

  const clearState = () => {
    setName(undefined);
    setGoodsData(undefined);
    onClose();
  }
  const getGoodsData = async () => {
    try {
      const sendData = { originalId: id };
      const query = await ky.post(`${apiURL}get_goods_data_using_original_id`, { json: sendData }).json() as any;
      const data = query.data as Omit<GoodsDataType, 'goods'>;
      const goods = data.items.goods[0] as GoodsDb & { id: string; };
      console.log(data)
      setGoodsData({ ...data, goods });
      setName(goods.name);
      setStatus(goods.status);
      setThisVersionInNumber(data.thisInfo[0]);

      const newItemDataList: Hash<{ dataKind: string; }> = {};
      Object.keys(data.items).forEach(snakeKey => {
        const values = data.items[snakeKey];
        const key = toCamelCase(snakeKey);
        values.forEach(value => {
          if ('original_id' in value && typeof value.original_id === 'string' &&
              'jdoc' in value && typeof value.jdoc === 'string'
          ) newItemDataList[value.original_id] = { ...JSON.parse(value.jdoc), dataKind: key };
        })
      })
      setItemDataList(newItemDataList);
      console.log(newItemDataList)
    } catch (err) {
      console.log(err);
    }
  }
  const publishExe = async () => {
    if (!publishData || !name) return;
    try {
      const sendData = { user_id: getCookie('user_id'), name, kind: goodsKind, hash: publishData };
      const query = await ky.post(`${apiURL}make_goods`, { json: sendData }).json() as any;
      console.log(query.data)
      dispatch(setSnackbar({ open: true, message: `${name} を商品化しました` }))
      clearState();
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (id) getGoodsData();
    const value = makePublishData(goodsKind, id);
    setPublishData(makePublishData(goodsKind, id));
    if (id && value) setName(value.defaultName);
  }, [id])

  useEffect(() => {
    console.log(goodsData)
    if (!publishData || !goodsData) return;
    console.log(itemDataList)
    console.log(publishData)

    type AnyObject = Hash<any>;
    const deepDiff = (a?: AnyObject, b?: AnyObject): AnyObject => {
      if (!a) {
        if (b && 'name' in b) return { 'component_status': 'added', 'name': b.name };
        else return { 'component_status': 'added' };
      } else if (!b) return { 'component_status': 'deleted' };
      const result: AnyObject = {};
      const keys = Array.from(
        new Set([...Object.keys(a), ...Object.keys(b)])
      );
      for (const key of keys) {
        if (key !== 'dataKind') {
          const valA = a[key];
          const valB = b[key];

          if (isObject(valA) && isObject(valB)) {
            const diff = deepDiff(valA, valB);
            if (Object.keys(diff).length > 0) {
              result[key] = diff;
            }
          } else if (!isEqual(valA, valB)) {
            result[key] = valB;
          }
        }
      }
      return result;
    }
    const isObject = (value: any): value is AnyObject => {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
    const isEqual = (a: any, b: any): boolean => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((val, i) => isEqual(val, b[i]));
      }
      return a === b;
    }

    const newUpdateData: Hash<Hash<Object>> = {};
    const newChangeLog: Hash<{ dataKind: string; nestIds: string[]; component_status?: 'add' | 'deleted'; }> = {};
    const nestIdsList: Hash<string[]> = {};
    const newPrimeIds: string[] = [];

    Object.keys(publishData).forEach(key => {
      if (!isCorrectKey(publishData, key)) return;
      newUpdateData[key] = {};
      const parts = publishData[key];
      if (typeof parts === 'string') return;
      const snakeKey = toSnakeCase(key);
      const items = goodsData.items[snakeKey];
      const itemIds: string[] = [];
      items.forEach(value => ('original_id' in value && typeof value.original_id === 'string') && itemIds.push(value.original_id));
      const ids = Array.from(new Set(Object.keys(parts).concat(itemIds)));
      ids.forEach(id => {
        const newData = parts[id];
        const data = itemDataList[id];
        const diff = deepDiff(data, newData);

        const component = items ? items.find(item => 'original_id' in item && item.original_id === id) : null;
        if (!data) newUpdateData[key][id] = { ...diff, value: newData };
        else if (component && 'component_id' in component) {
          if (!newData) newUpdateData[key][id] = { ...diff, value: {}, componentId: component.component_id };
          else newUpdateData[key][id] = { value: diff, componentId: component.component_id };
        }

        if (Object.keys(diff).length >= 1) {
          newChangeLog[id] = { ...diff, dataKind: key, nestIds: [] };

          if (newData) {
            if ('cardId' in newData && typeof newData.cardId === 'string') {
              if (!nestIdsList[newData.cardId]) nestIdsList[newData.cardId] = [];
              nestIdsList[newData.cardId].push(id);
            } else if ('sheetId' in newData && typeof newData.sheetId === 'string') {
              if (!nestIdsList[newData.sheetId]) nestIdsList[newData.sheetId] = [];
              nestIdsList[newData.sheetId].push(id);
            } else if ('cardTemplateId' in newData) newPrimeIds.push(id);
            if (key === 'images' && !data) newPrimeIds.push(id);
          } else if (data) {
            if ('cardId' in data && typeof data.cardId === 'string') {
              if (!nestIdsList[data.cardId]) nestIdsList[data.cardId] = [];
              nestIdsList[data.cardId].push(id);
            } else if ('sheetId' in data && typeof data.sheetId === 'string') {
              if (!nestIdsList[data.sheetId]) nestIdsList[data.sheetId] = [];
              nestIdsList[data.sheetId].push(id);
            } else if ('cardTemplateId' in data) newPrimeIds.push(id);
            if (key === 'images') newPrimeIds.push(id);
          }
        }
      })
    })

    const IdsHaveParent: string[] = [];

    Object.keys(nestIdsList).forEach(parentId => {
      IdsHaveParent.push(...nestIdsList[parentId]);
      const data = itemDataList[parentId];
      console.log(data)
      if (!newChangeLog[parentId]) {
        if (data) newChangeLog[parentId] = { dataKind: data.dataKind, nestIds: nestIdsList[parentId] };
      } else newChangeLog[parentId].nestIds.push(...nestIdsList[parentId]);
      if (data && 'sheetId' in data && typeof data.sheetId === 'string') {
        const sheetData = itemDataList[data.sheetId];
        if (!newChangeLog[data.sheetId] && sheetData) newChangeLog[data.sheetId] = { dataKind: sheetData.dataKind, nestIds: [parentId] };
        else if (!newChangeLog[data.sheetId].nestIds.includes(parentId)) newChangeLog[data.sheetId].nestIds.push(parentId);
      }
    })

    newPrimeIds.push(...Object.keys(nestIdsList).filter(targetId => !IdsHaveParent.includes(targetId)));

    console.log(newUpdateData)
    console.log(newChangeLog)
    console.log(newPrimeIds)
    setUpdateData(newUpdateData);
    setChangeLog(newChangeLog);
    setPrimeIds(newPrimeIds);

  }, [itemDataList])

  return (
    <>
      <Dialog open={open} onClose={clearState}>
        <DialogTitle>{
          goodsData ? 'ショップに公開済みの商品をアップデートする' : 'ショップの商品として公開する'
        }</DialogTitle>
        <DialogContent>
          { goodsData &&
            <div>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Typography variant="caption">商品名</Typography>
                  <Typography variant="body1">{ name }</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">公開状態</Typography>
                  <Typography variant="body1">{ publishStatus[status] }</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">このデータをもとにしたバージョン</Typography>
                  <Typography variant="body1">{ formatVersion(goodsData.thisInfo[0]) }</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">このデータのID</Typography>
                  <Typography variant="body1">{ goodsData.thisInfo[1] }</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">変更差分</Typography>
                  { Object.keys(changeLog).length === 0 ? (
                    <Typography variant="body1">変更なし</Typography>
                  ) : (
                    <Card style={{ marginTop: '4px', padding: '4px 8px 8px 8px', maxHeight: '240px', maxWidth: '74%', overflow: 'auto' }}>
                      <ChangeLogList changeLog={changeLog} ids={primeIds} itemDataList={itemDataList} />
                    </Card>
                  ) }
                </Grid>
              </Grid>
              <Grid container style={{ marginTop: 32 }}>
                <Grid item xs={6}>
                  <Typography variant="caption">最新の同一メジャーバージョン</Typography>
                  <Typography variant="body1">{ formatVersion(goodsData.majorLatestInfo[0]) }</Typography>
                  <Typography variant="body1">{ goodsData.majorLatestInfo[2] }（{ goodsData.majorLatestInfo[1] }）</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">最新バージョン</Typography>
                  <Typography variant="body1">{ formatVersion(goodsData.allLatestInfo[0]) }</Typography>
                  <Typography variant="body1">{ goodsData.allLatestInfo[2] }（{ goodsData.allLatestInfo[1] }）</Typography>
                </Grid>
              </Grid>
            </div>
          }
          { !goodsData &&
            <TextField label="商品名" variant="outlined" value={name} onChange={ev => setName(ev.currentTarget.value)} />
          }
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={clearState} size="large">
            キャンセル
          </Button>
          { goodsData ? (
            <Button color="primary" onClick={() => setSelectVersionAction(true)} size="large"
              disabled={Object.keys(changeLog).length === 0}
            >
              アップデートする
            </Button>
          ) : (
            <Button color="primary" onClick={publishExe} size="large">
              公開する
            </Button>
          ) }
        </DialogActions>
      </Dialog>
      <SelectVersionDialog open={selectVersionAction}
        onClose={done => {
          setSelectVersionAction(false);
          if (done) clearState();
        }}
        goodsData={goodsData}
        updateData={updateData}
        thisVersion={thisVersionInNumber}
        publishData={publishData}
      />
    </>
  );
}

export default PublishDialog;
